import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-6">
          <div className="logo-placeholder"></div>
          <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="flex-1 p-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
            <p className="text-gray-600">Last updated: December 10, 2025</p>
          </div>

          <Card className="bg-white border-gray-200 shadow-md p-8">
            <div className="prose prose-gray max-w-none space-y-6">
              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700">
                  By accessing and using Podmixr Labs ("Service"), you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
                <p className="text-gray-700">
                  Podmixr Labs provides a cloud-based podcast recording, editing, and enhancement platform. Our Service includes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
                  <li>Audio and video recording capabilities</li>
                  <li>Live teleprompter functionality</li>
                  <li>AI-powered audio and video enhancement (Pro tier)</li>
                  <li>Transcript generation and AI-powered editing (Pro tier)</li>
                  <li>Media composition tools (intro/outro/music)</li>
                  <li>Cloud storage for your recordings</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Registration</h3>
                <p className="text-gray-700">
                  To use certain features, you must create an account. You agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-2">
                  <li>Provide accurate, current, and complete information</li>
                  <li>Maintain and update your information</li>
                  <li>Keep your password secure and confidential</li>
                  <li>Notify us immediately of any unauthorized access</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">3.2 Account Responsibility</h3>
                <p className="text-gray-700">
                  You are responsible for all activities that occur under your account. We are not liable for any loss or damage arising from your failure to maintain account security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Subscription Plans</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Free Tier</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>5 recordings per month</li>
                  <li>Basic recording and editing features</li>
                  <li>Manual transcripts</li>
                  <li>Standard downloads (MP4, MP3, TXT)</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">4.2 Pro Tier (£9.99/month)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Unlimited recordings</li>
                  <li>AI-powered audio enhancement</li>
                  <li>AI-powered video enhancement</li>
                  <li>AI transcript editing</li>
                  <li>Priority support</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">4.3 Payment Terms</h3>
                <p className="text-gray-700">
                  Pro subscriptions are billed monthly in British Pounds (GBP) via Stripe. By subscribing, you authorize us to charge your payment method. Subscriptions automatically renew unless cancelled. You may cancel at any time, and cancellation will take effect at the end of your current billing period.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Acceptable Use</h2>
                <p className="text-gray-700 mb-3">You agree NOT to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Upload content that is illegal, harmful, or violates others' rights</li>
                  <li>Use the Service for harassment, abuse, or hate speech</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Distribute malware, viruses, or harmful code</li>
                  <li>Scrape, copy, or reverse engineer the Service</li>
                  <li>Share your account credentials with others</li>
                  <li>Use the Service for any commercial purpose without our consent</li>
                  <li>Violate any applicable laws or regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Content Ownership and Rights</h2>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Your Content</h3>
                <p className="text-gray-700">
                  You retain all ownership rights to the content you create (recordings, scripts, etc.). By using our Service, you grant us a limited license to store, process, and display your content solely to provide the Service to you.
                </p>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">6.2 Our Platform</h3>
                <p className="text-gray-700">
                  All rights, title, and interest in the Service (including software, design, trademarks) remain our property. You may not copy, modify, or create derivative works of our platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. AI Processing</h2>
                <p className="text-gray-700">
                  Our AI features process your audio, video, and transcript content to provide enhancement and editing services. By using these features, you consent to this processing. We do not use your content to train AI models or for any purpose other than providing the requested services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Termination</h2>
                <p className="text-gray-700">
                  We may suspend or terminate your account if you violate these Terms or engage in conduct that we determine is harmful to other users or the Service. You may terminate your account at any time through your account settings. Upon termination, your right to use the Service ceases immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimers</h2>
                <p className="text-gray-700">
                  THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. WE ARE NOT RESPONSIBLE FOR:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-2">
                  <li>Loss of content due to technical failures</li>
                  <li>Quality of AI-enhanced content</li>
                  <li>Compatibility with all devices or browsers</li>
                  <li>Third-party services (Firebase, Stripe)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
                <p className="text-gray-700">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, PODMIXR LABS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
                <p className="text-gray-700">
                  You agree to indemnify and hold harmless Podmixr Labs from any claims, damages, or expenses arising from your use of the Service, your content, or your violation of these Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
                <p className="text-gray-700">
                  We may modify these Terms at any time. We will notify you of significant changes via email or platform notice. Continued use after changes constitutes acceptance of the modified Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
                <p className="text-gray-700">
                  These Terms are governed by the laws of [Your Jurisdiction]. Any disputes shall be resolved in the courts of [Your Jurisdiction].
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact</h2>
                <p className="text-gray-700">
                  For questions about these Terms, contact us at support@podmixrlabs.com
                </p>
              </section>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-8">
                <p className="text-sm text-yellow-900">
                  <strong>Legal Notice:</strong> This is a template. For full legal protection, have these terms reviewed by a qualified attorney in your jurisdiction.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
}
